import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axiosInstance from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { normalizeCountryCode } from "../../utils/countries";
import { stripeCardElementOptions } from "../../components/custom/StripePaymentSection.jsx";
import { useClearScopedAccountOnMount } from "../../utils/useClearScopedAccountOnMount";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_live_51TcWrnEFp6E1oRogJ6ul5KZwwOONiiQlsq1WfQiz6KFzwtF6k4ktPHC1ykVZiWZd67Wl2S9UA71VlHxMEJBddZmL00RkevipOk"
);

const cardRadius = { borderRadius: "14px" };

const CREATE_PAGE_STYLES = `
  .accounts-page {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 6.5rem);
  }
  .accounts-section-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6c757d;
  }
  .accounts-card-input {
    border-radius: 8px;
  }
  .accounts-pay-panel {
    border: 1px solid #e9ecef;
    border-radius: 12px;
    background: #f8fafc;
  }
`;

function CreateCompanyAccountForm() {
  useClearScopedAccountOnMount();
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [packageId, setPackageId] = useState("");
  const [pondCount, setPondCount] = useState(1);
  const [country, setCountry] = useState("US");
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("api/Packages/GetAll");
        const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const active = list.filter((p) => p.Status && Number(p.Amount) > 0);
        setPackages(active);
        if (active[0]) {
          setPackageId(String(active[0].id));
          setPondCount(active[0].IncludedPonds ?? 1);
        }
      } catch (e) {
        showErrorToast("Could not load packages");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedPkg = packages.find((p) => String(p.id) === String(packageId));

  const startCheckout = useCallback(async () => {
    if (!fullName.trim() || !email.trim() || password.length < 8 || !packageId) {
      showErrorToast("Fill owner name, email, password (8+), and package");
      return null;
    }
    const res = await axiosInstance.post("api/company/accounts/checkout", {
      email: email.trim(),
      fullName: fullName.trim(),
      packageId: parseInt(packageId, 10),
      pondCount: parseInt(pondCount, 10) || 1,
      country: normalizeCountryCode(country) || "US",
      replaceSubscriptionId: checkout?.subscriptionId || undefined,
    });
    setCheckout(res.data);
    return res.data;
  }, [fullName, email, password, packageId, pondCount, country, checkout?.subscriptionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!fullName.trim() || !email.trim() || password.length < 8 || !packageId) {
      showErrorToast("Fill owner name, email, password (8+), and package");
      return;
    }
    const ponds = parseInt(pondCount, 10);
    if (!Number.isFinite(ponds) || ponds < 1) {
      showErrorToast("Licensed ponds must be at least 1");
      return;
    }
    if (ponds > 50) {
      showErrorToast("Licensed ponds cannot exceed 50");
      return;
    }
    setSubmitting(true);
    try {
      let checkoutData = checkout;
      if (!checkoutData?.clientSecret) {
        checkoutData = await startCheckout();
        if (!checkoutData) {
          setSubmitting(false);
          return;
        }
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        showErrorToast("Card form is not ready");
        setSubmitting(false);
        return;
      }

      const confirmFn =
        checkoutData.clientSecretType === "setup"
          ? stripe.confirmCardSetup
          : stripe.confirmCardPayment;

      const result = await confirmFn(checkoutData.clientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: checkoutData.customerName || fullName.trim(),
            email: checkoutData.customerEmail || email.trim(),
          },
        },
      });

      if (result.error) {
        showErrorToast(result.error.message || "Payment failed");
        setSubmitting(false);
        return;
      }

      const paymentIntentId =
        result.paymentIntent?.id ||
        checkoutData.paymentIntentId ||
        result.setupIntent?.id ||
        checkoutData.setupIntentId;

      await axiosInstance.post("api/company/accounts", {
        FullName: fullName.trim(),
        Email: email.trim(),
        Password: password,
        PackageID: parseInt(packageId, 10),
        LicensedPondCount: checkoutData.licensedPondCount || parseInt(pondCount, 10) || 1,
        AmountPaid: checkoutData.amount,
        StripePaymentIntentId: paymentIntentId,
        StripeSubscriptionId: checkoutData.subscriptionId,
        Country: normalizeCountryCode(country) || "US",
      });

      showSuccessToast("Pond owner account created");
      history.push("/company/accounts");
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Could not create account"
      );
      setCheckout(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Preloader />;

  return (
    <div className="accounts-page">
      <style>{CREATE_PAGE_STYLES}</style>

      <form onSubmit={handleSubmit}>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ul className="breadcrumb mb-2">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/company/accounts">Accounts</Link>
              </li>
              <li className="breadcrumb-item active">Create account</li>
            </ul>
            <h1 className="page-header mb-1">Create account</h1>
            <p className="text-muted small mb-0">
              You pay for this account&apos;s package and licensed ponds. The owner will not manage billing.
            </p>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3" style={cardRadius}>
          <div className="card-body p-4">
            <div className="accounts-section-label mb-3">Owner details</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="createOwnerFullName">
                  Owner full name
                </label>
                <input
                  id="createOwnerFullName"
                  className="form-control accounts-card-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="createOwnerEmail">
                  Owner email (login)
                </label>
                <input
                  id="createOwnerEmail"
                  type="email"
                  className="form-control accounts-card-input"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setCheckout(null);
                  }}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="createOwnerPassword">
                  Owner password
                </label>
                <input
                  id="createOwnerPassword"
                  type="password"
                  className="form-control accounts-card-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
                <div className="form-text">At least 8 characters.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3" style={cardRadius}>
          <div className="card-body p-4">
            <div className="accounts-section-label mb-3">Package</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="createPackage">
                  Package
                </label>
                <select
                  id="createPackage"
                  className="form-select accounts-card-input"
                  value={packageId}
                  onChange={(e) => {
                    setPackageId(e.target.value);
                    setCheckout(null);
                    const pkg = packages.find((p) => String(p.id) === e.target.value);
                    if (pkg) setPondCount(pkg.IncludedPonds ?? 1);
                  }}
                  required
                >
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.Name} — ${p.Amount}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="createPondCount">
                  Licensed ponds
                </label>
                <input
                  id="createPondCount"
                  type="number"
                  min={1}
                  max={50}
                  className="form-control accounts-card-input"
                  value={pondCount}
                  onChange={(e) => {
                    setPondCount(e.target.value);
                    setCheckout(null);
                  }}
                  required
                />
                {selectedPkg?.AllowsExtraPonds ? (
                  <div className="form-text">
                    Includes {selectedPkg.IncludedPonds ?? 1}; extra ponds $
                    {selectedPkg.ExtraPondAmount}/each
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm" style={cardRadius}>
          <div className="card-body p-4">
            <div className="accounts-section-label mb-3">Payment</div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="createBillingCountry">
                  Billing country
                </label>
                <input
                  id="createBillingCountry"
                  className="form-control accounts-card-input"
                  value={country}
                  maxLength={2}
                  onChange={(e) => {
                    setCountry(e.target.value.toUpperCase());
                    setCheckout(null);
                  }}
                />
              </div>
            </div>

            <div className="accounts-pay-panel p-3 mb-4">
              <label className="form-label fw-semibold mb-2">Card details (company payment)</label>
              <CardElement options={stripeCardElementOptions} />
              {checkout?.recurringLabel ? (
                <div className="form-text mt-2">{checkout.recurringLabel}</div>
              ) : (
                <div className="form-text mt-2">
                  Checkout starts when you pay. The company card is charged for this pond owner.
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !stripe}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin me-2" />
                  Processing…
                </>
              ) : (
                "Pay & create account"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const CreateCompanyAccount = () => (
  <Elements stripe={stripePromise}>
    <CreateCompanyAccountForm />
  </Elements>
);

export default CreateCompanyAccount;
